import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import BookingFeeCheckout from "@/components/booking-fee-checkout";
import { BookingFeePayinSessionProvider } from "@/components/rainforest/booking-fee-payin-session-provider";
import { useRainforestPayinSessionContext } from "@/components/rainforest/rainforest-payin-session-context";
import { Button } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { useExpiryTimer } from "@/hooks/use-expiry-timer";
import { formatCardOnFilePolicy } from "@/lib/card-on-file-policy";
import { flattenSelectedServices } from "@/lib/service-addon-utils";
import { computeBookingFeeCreditCents, computeTotalBookingFeeCents } from "@/lib/service-fees";
import { cx } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { chargeBookingFeeWithSavedMethod } from "@/api/provider/billing/payment-link";
import { useSelfScheduleStore } from "@/stores/self-schedule-store";

// Mirrors decoda/common/booking_fee.py:RESERVED_FALLBACK_MINUTES.
const DEFAULT_RESERVED_MINUTES = 20;
interface CheckoutProps {
  className?: string;
}
interface SnapshotBookingFeeCheckoutProps extends CheckoutProps {
  bookingFeeCents: number;
  fallbackCreditAmountCents: number;
  refundableBeforeHours: number | null;
  bookingFeeText: string | null;
  cardPolicy?: string;
  timeLeft: number;
  patientId: string;
  payWithSavedMethod: (paymentMethodId: string) => Promise<void>;
  onComplete: () => void;
}
const SnapshotBookingFeeCheckout = t0 => {
  const $ = _c(18);
  const {
    bookingFeeCents,
    fallbackCreditAmountCents,
    refundableBeforeHours,
    bookingFeeText,
    cardPolicy,
    timeLeft,
    patientId,
    payWithSavedMethod,
    onComplete,
    className
  } = t0;
  const {
    payinResponse,
    isLoadingSession,
    isError
  } = useRainforestPayinSessionContext();
  const readyPayin = payinResponse && "state" in payinResponse && payinResponse.state === "READY" ? payinResponse : null;
  if (isError) {
    let t1;
    if ($[0] !== className) {
      t1 = cx("flex flex-col items-center space-y-4 py-12", className);
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    let t3;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <FaExclamationTriangle className="h-10 w-10 text-status-error-text" />;
      t3 = <p className="max-w-md text-center text-foreground-secondary">We couldn't load your payment details. Please refresh the page to try again.</p>;
      $[2] = t2;
      $[3] = t3;
    } else {
      t2 = $[2];
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t1) {
      t4 = <div className={t1}>{t2}{t3}</div>;
      $[4] = t1;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    return t4;
  }
  const t1 = readyPayin?.feeAmountCents ?? bookingFeeCents;
  const t2 = isLoadingSession || !readyPayin;
  const t3 = readyPayin ? readyPayin.hoursBeforeBookingFeeRefundable : refundableBeforeHours;
  const t4 = readyPayin ? readyPayin.bookingFeeText : bookingFeeText;
  const t5 = readyPayin?.bookingFeeCreditAmountCents ?? fallbackCreditAmountCents;
  let t6;
  if ($[6] !== cardPolicy || $[7] !== className || $[8] !== onComplete || $[9] !== patientId || $[10] !== payWithSavedMethod || $[11] !== t1 || $[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== timeLeft) {
    t6 = <BookingFeeCheckout bookingFeeCents={t1} isAmountLoading={t2} refundableBeforeHours={t3} bookingFeeText={t4} cardPolicy={cardPolicy} bookingFeeCreditAmountCents={t5} timeLeft={timeLeft} patientId={patientId} payWithSavedMethod={payWithSavedMethod} onComplete={onComplete} className={className} />;
    $[6] = cardPolicy;
    $[7] = className;
    $[8] = onComplete;
    $[9] = patientId;
    $[10] = payWithSavedMethod;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = timeLeft;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
const Checkout = ({
  className
}: CheckoutProps) => {
  const {
    selectedServices,
    bookingFeeMode,
    createdAppointmentId,
    paymentDeadline,
    setPaymentSuccessful,
    setSelectedDateTime,
    setCreatedAppointmentId,
    setPaymentDeadline
  } = useSelfScheduleContext();
  const {
    claimedPatient: patient
  } = usePatientClaim();
  const {
    selectedAddons
  } = useSelfScheduleStore();
  const feeServices = useMemo(() => flattenSelectedServices(selectedServices ?? [], selectedAddons), [selectedAddons, selectedServices]);
  const bookingFeeAmount = useMemo(() => computeTotalBookingFeeCents(feeServices, bookingFeeMode), [feeServices, bookingFeeMode]);
  const bookingFeeCreditAmount = useMemo(() => computeBookingFeeCreditCents(feeServices, bookingFeeMode), [feeServices, bookingFeeMode]);
  const {
    data: cancellationPolicy
  } = useQuery(selfScheduleQueryOptions.cancellationPolicy(feeServices.map(service => service.id)));
  const minRefundableHours = useMemo(() => {
    if (!selectedServices) return Infinity;
    return feeServices.reduce((acc, service_0) => Math.min(acc, service_0.hoursBeforeBookingFeeRefundable ?? Infinity), Infinity);
  }, [feeServices, selectedServices]);

  // Zero-fee shortcut: nothing to collect, advance the wizard.
  useEffect(() => {
    if (bookingFeeAmount <= 0 && selectedServices) {
      setPaymentSuccessful(true);
    }
  }, [bookingFeeAmount, selectedServices, setPaymentSuccessful]);

  // Set the payment deadline on mount based on the shortest reservedTimeMinutes
  // across selected services.
  useEffect(() => {
    if (bookingFeeAmount > 0 && selectedServices && !paymentDeadline) {
      const minutes = feeServices.reduce((min, service_1) => service_1.reservedTimeMinutes != null && service_1.reservedTimeMinutes > 0 ? Math.min(min, service_1.reservedTimeMinutes) : min, Infinity);
      const durationMinutes = minutes !== Infinity ? minutes : DEFAULT_RESERVED_MINUTES;
      setPaymentDeadline(new Date(Date.now() + durationMinutes * 60 * 1000));
    }
  }, [bookingFeeAmount, feeServices, selectedServices, paymentDeadline, setPaymentDeadline]);
  const {
    timeLeft,
    isExpired
  } = useExpiryTimer(paymentDeadline);
  const sessionEnabled = Boolean(selectedServices && createdAppointmentId && patient) && bookingFeeAmount > 0 && !isExpired;
  const handlePayWithSavedMethod = async (paymentMethodId: string) => {
    if (!createdAppointmentId) throw new Error("Appointment not yet created");
    try {
      const result = await chargeBookingFeeWithSavedMethod(createdAppointmentId, paymentMethodId);
      if (!result) throw new Error("Charge failed");
    } catch (err) {
      toast({
        title: "Payment failed",
        description: err instanceof Error ? err.message : "Payment failed. Please try again.",
        variant: "error"
      });
      throw err;
    }
  };
  if (!selectedServices || !createdAppointmentId || !patient) {
    return null;
  }
  const handleReschedule = () => {
    setSelectedDateTime(null);
    setCreatedAppointmentId(null);
    setPaymentDeadline(null);
  };
  if (isExpired) {
    return <div className={cx("motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", className)} key="checkout-expired-container">
        <div className="flex flex-col items-center justify-center space-y-6 py-12">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-status-error-bg">
            <FaExclamationTriangle className="h-12 w-12 text-status-error-text" />
          </div>
          <div className="text-center">
            <h1 className="mb-2 text-h1 text-foreground">Payment Timeout</h1>
            <p className="max-w-md text-foreground-secondary">
              The payment window has expired and your appointment reservation
              has been cancelled. Please select a new time to reschedule.
            </p>
          </div>
          <Button variant="primary" className="rounded-lg bg-primary px-6 py-3 text-primary-foreground transition-colors hover:bg-primary/90" onClick={handleReschedule}>
            <span>Reschedule Appointment</span>
          </Button>
        </div>
      </div>;
  }
  return <BookingFeePayinSessionProvider eventId={createdAppointmentId} patientId={patient.id} patient={patient} enabled={sessionEnabled}>
      <SnapshotBookingFeeCheckout bookingFeeCents={bookingFeeAmount} fallbackCreditAmountCents={bookingFeeCreditAmount} refundableBeforeHours={minRefundableHours !== Infinity ? minRefundableHours : null} bookingFeeText={selectedServices[0]?.bookingFeeText ?? null} cardPolicy={cancellationPolicy ? formatCardOnFilePolicy(cancellationPolicy) : undefined} timeLeft={timeLeft} patientId={patient.id} payWithSavedMethod={handlePayWithSavedMethod} onComplete={() => setPaymentSuccessful(true)} className={className} />
    </BookingFeePayinSessionProvider>;
};
export default Checkout;